import React, { useEffect, useState } from 'react';
import { Row, Col, Collapse } from 'antd';
import { I18n } from '@aws-amplify/core';

import FormItem from '@/components/ui/FormItem';
import Textarea from '@/components/ui/Textarea';
import { InfoBox } from '../../components/InfoBox';
import { Pagination } from '../../components/Pagination';
import InputRadio from '@/components/ui/InputRadio';
import ButtonDefault from '@/components/ui/ButtonDefault';
import { useActionPlan } from '../../components/ActionPlan/_hooks';
import { ActionPlanTable } from '../../components/ActionPlan/Table';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import { TableActionPlanReview } from '../../components/ActionPlan/TableActionPlanReview';
import { useGetResolution886ReportReview } from '@/hooks/v2/resolution-886/useGetResolution886ReportReview';
import { ToastError } from '../../components/ToastError';
import { AssessmentResponse } from '@/context/Resolution886Report/types';
import { StepsResolution886 } from '../../enums';
import { useActionPlanStep } from './hooks';
import { Activity } from '@/types';
import * as S from './styles';

interface Assessment extends AssessmentResponse {
	activity: Activity;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

const { Panel } = Collapse;

export function ActionPlanStep(): JSX.Element {
	const [showToast, setShowToast] = useState<boolean>(false);
	const [activeKey, setActiveKey] = useState<string | string[]>([]);

	const [activityId, setActivityId] = useState<string>();
	const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);

	const [activity, setActivity] = useState<Activity>();
	const [activityList, setActivityList] = useState<Assessment[]>([]);

	const { organization, company } = useApplicationContext();
	const { setAssessment, lastReviewId, report, newReview, previousReview } = useResolution886Report();

	const {
		isLoading: isLoadingSavePreventiveMeasures,
		handleSavePreventiveMeasures,
		handleUpdateStep
	} = useActionPlanStep();

	const {
		isSuccess,
		data: review,
		isFetching: isFetchingReview
	} = useGetResolution886ReportReview({
		organization_id: organization?.id!,
		company_id: company?.id!,
		review_id: lastReviewId!,
		report_id: report?.id!
	});

	const { isLoading, data, handleOnDelete, validateActionPlanByRisk } = useActionPlan({
		organization_id: organization?.id!,
		company_id: company?.id!,
		review_id: lastReviewId!,
		report_id: report?.id!,
		activity_id: activityId!
	});

	useEffect(() => {
		if (review && isSuccess) {
			const assessments = review?.assessments?.filter((value) => value.activity?.id!);
			setActivityList(assessments as unknown as Assessment[]);

			const [firstAssessment] = assessments;

			if (!firstAssessment) {
				return;
			}

			const { activity: firstActivity, ...assessment } = firstAssessment;

			setAssessment(assessment as any);
			setActivity(firstActivity as Activity);
			setActivityId(firstActivity.id);
		}
	}, [review, isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

	function handleOnNextStep(): void {
		if (validateActionPlanByRisk()) {
			setShowToast(true);
			setTimeout(() => setShowToast(false), 5000);
			return;
		}

		handleSavePreventiveMeasures();
	}

	function handleOnToggleCollapse(): void {
		setActiveKey(activeKey.length ? [] : ['1']);
	}

	function handleOnChangePage(page: number): void {
		const element = activityList[page - 1];
		setCurrentPage(page);
		setActivity(element.activity);
		setActivityId(element.activity.id);
		const { activity, ...newAssessment } = element;
		setAssessment(newAssessment as AssessmentResponse);
	}

	const count = activityList.length * DEFAULT_PAGE_SIZE;

	const isLoadingAll = isLoadingSavePreventiveMeasures || isFetchingReview || isLoading;

	return (
		<>
			{showToast && (
				<ToastError message="Please develop action plans for all not tolerable and moderate risks before proceeding" />
			)}
			<S.DefaultRowContent gutter={[32, 16]} align="top">
				<Col xs={24} md={24} style={{ marginBottom: '20px' }}>
					<S.DefaultStepTitle>{I18n.get('Action plan')}</S.DefaultStepTitle>
					<S.DefaultStepSubTitle>
						{I18n.get(
							'Answer the previous questions, create and review the action plans (corrective and preventive measures)'
						)}
					</S.DefaultStepSubTitle>
					{(newReview || previousReview?.id) && (
						<>
							<S.CustomDivMargin>
								<S.CustomButtonDefault
									type="default"
									onClick={handleOnToggleCollapse}
									className={activeKey.length ? 'active' : ''}
								>
									{I18n.get('View previous evaluation')}
								</S.CustomButtonDefault>
							</S.CustomDivMargin>
							<S.CustomCollapse activeKey={activeKey} bordered={false}>
								<Panel key="1" showArrow={false} header={null}>
									<Row gutter={[48, 16]}>
										<Col span={24}>
											<TableActionPlanReview />
										</Col>
									</Row>
								</Panel>
							</S.CustomCollapse>
						</>
					)}
				</Col>
				<Col xs={24} md={24} lg={12}>
					<S.HeadingSmall style={{ marginBottom: '25px' }}>
						{I18n.get('General preventive measures')}
					</S.HeadingSmall>
					<FormItem
						required
						requiredMessage={I18n.get('This field is required')}
						name={['measures', 'risk_information', 'result']}
						label={I18n.get(
							'The worker(s), supervisor(s), engineer(s), and management related to the workstation have been informed about the risk associated with the task of developing MSD'
						)}
					>
						<InputRadio
							options={[
								{ label: I18n.get('Yes'), value: 'yes' },
								{ label: I18n.get('No'), value: 'no' }
							]}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<S.HeadingSmall>{I18n.get('Observations')}</S.HeadingSmall>
					<FormItem name={['measures', 'risk_information', 'observations']}>
						<Textarea
							showCount
							maxLength={500}
							placeholder={I18n.get('Observations')}
							autoSize={{ minRows: 2, maxRows: 10 }}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<FormItem
						required
						requiredMessage={I18n.get('This field is required')}
						name={['measures', 'symptom_training', 'result']}
						label={I18n.get(
							'The worker(s) and supervisor(s) related to the workstation have been trained on identifying symptoms related to the development of MSD'
						)}
					>
						<InputRadio
							options={[
								{ label: I18n.get('Yes'), value: 'yes' },
								{ label: I18n.get('No'), value: 'no' }
							]}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<S.HeadingSmall>{I18n.get('Observations')}</S.HeadingSmall>
					<FormItem name={['measures', 'symptom_training', 'observations']}>
						<Textarea
							showCount
							maxLength={500}
							placeholder={I18n.get('Observations')}
							autoSize={{ minRows: 2, maxRows: 10 }}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<FormItem
						required
						name={['measures', 'symptom_identification', 'result']}
						requiredMessage={I18n.get('This field is required')}
						label={I18n.get(
							'The worker(s) and supervisor(s) related to the workstation have been trained on the measures and/or procedures to prevent the development of MSD'
						)}
					>
						<InputRadio
							options={[
								{ label: I18n.get('Yes'), value: 'yes' },
								{ label: I18n.get('No'), value: 'no' }
							]}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<S.HeadingSmall>{I18n.get('Observations')}</S.HeadingSmall>
					<FormItem name={['measures', 'symptom_identification', 'observations']}>
						<Textarea
							showCount
							maxLength={500}
							autoSize={{ minRows: 2, maxRows: 10 }}
							placeholder={I18n.get('Observations')}
							disabled={isLoadingAll}
						/>
					</FormItem>
				</Col>
				<Col xs={24} md={24} lg={12}>
					<S.HeadingSmall style={{ marginBottom: '25px' }}>
						{I18n.get('Specific corrective and preventive measures')}
					</S.HeadingSmall>
				</Col>
				<S.CustomColMargin span={24}>
					<InfoBox
						type="info"
						title={I18n.get(
							'Action plans must be developed for risks classified as moderate and not tolerable.'
						)}
					/>
				</S.CustomColMargin>
				<Col span={24}>
					<ActionPlanTable
						title={activity?.name}
						loading={isLoadingAll}
						data={data}
						onDelete={handleOnDelete}
					/>
				</Col>
				<S.CustomColMargin span={24} margin="20px 0 0 0">
					<Pagination limit={10} currentPage={currentPage} total={count} onChangePage={handleOnChangePage} />
				</S.CustomColMargin>
			</S.DefaultRowContent>
			<S.CustomRowPadding gutter={[16, 16]}>
				<S.CustomColFlex span={24}>
					<ButtonDefault
						type="default"
						onClick={() => handleUpdateStep(StepsResolution886.ASSESSMENT)}
						loading={isLoadingAll}
					>
						{I18n.get('Back')}
					</ButtonDefault>
					<ButtonDefault type="primary" onClick={handleOnNextStep} loading={isLoadingAll}>
						{I18n.get('Next')}
					</ButtonDefault>
				</S.CustomColFlex>
			</S.CustomRowPadding>
		</>
	);
}
